import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { HighlightedProps, SchoolReportType } from '../../types';
import { SectionLayout, getFilteredAverage, getOthersAverage } from '../modules';
import { StudentProgress } from '../../../components';
import type { ChartDataType, ChartIconType } from '../../../components/charts/types';
import { FIXED_AXIS } from '../../../components/charts/helpers/vars';

type Props = {
  schoolReport: SchoolReportType;
  showOthersData: boolean
  iconToUse: ChartIconType
} & HighlightedProps;

const SchoolQualityLearningTime = ({ schoolReport, showOthersData, highlightedLabel, highlightedIds, iconToUse, onElementClick }: Props): JSX.Element => {
  const { translate } = useI18n();

  const roundToDecimal = (value: number): string => {
    return Number.isSafeInteger(value) ? value.toFixed(0) : value.toFixed(2);
  };

  const dataToUse = (): ChartDataType[] => {
    if (iconToUse === 'class') {
      return schoolReport.classes;
    } else return schoolReport.yearGroups;
  };

  const value = roundToDecimal(schoolReport.average.qualityLearningTimeAverage);
  const highlightedAverage = getFilteredAverage(dataToUse(), highlightedIds, (group) => group.chartsData?.qualityLearningTime);
  const othersAverage = showOthersData ? getOthersAverage(dataToUse(), highlightedIds, (group) => group.chartsData?.qualityLearningTime) : null;

  return (
    <SectionLayout
      title={translate('pages.REPORTS.SECTIONS.QUALITY.title')}
      value={value}
      highlightLabel={highlightedLabel}
      highlightValue={highlightedAverage != null ? roundToDecimal(highlightedAverage) : null}
      othersLabel={showOthersData && highlightedIds.length > 0 ? translate('pages.REPORTS.HEADERS.OTHERS') : null}
      othersValue={othersAverage != null ? roundToDecimal(othersAverage) : null}
      unit={translate('pages.REPORTS.SECTIONS.QUALITY.unit', { count: parseFloat(value) })}
      description={translate('pages.REPORTS.SECTIONS.QUALITY.description')}
      moreInfo={translate('pages.REPORTS.SECTIONS.QUALITY.info')}
      id={'school-quality-learning-time-container'}
    >
      <StudentProgress
        showOthersData={showOthersData}
        data={dataToUse()}
        chartType={'qualityLearningTime'}
        type={iconToUse}
        highlightedIds={highlightedIds}
        customAxis={FIXED_AXIS.recent?.qualityLearningTime}
        onElementClick={onElementClick}
      />
    </SectionLayout>
  );
};

SchoolQualityLearningTime.displayName = 'SchoolQualityLearningTime';

export default SchoolQualityLearningTime;
